<script lang="ts" setup>
  import { useApi } from '@/composables/use-api.ts';
  import { router } from '@/routes/router.ts';
  import { ApiFlexError } from '@/services/api/api-error.ts';
  import { ApprovalApplicationWithManagerDocumentLineDto } from '@/services/api/approval-api.ts';
  import { convertSecondsToHoursAndMinutes } from '@/services/approval-application-svc.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { ApprovalStatusMap } from '@/stores/approval.ts';
  import { useThemeStore } from '@/stores/theme';
  import { showBackEndFlexErrorMessage } from '@/utils/error-message-helper.ts';
  import dayjs from 'dayjs';
  import { onMounted, ref } from 'vue';

  // path variable
  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
  });

  const themeStore = useThemeStore();
  const { approval } = useApi();
  const applicationId: number = Number(props.id);
  const loading = ref<boolean>(false);
  const application = ref<ApprovalApplicationWithManagerDocumentLineDto>();

  const formInputs = {
    comment: '',
  };

  async function refreshAsync() {
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        application.value = await approval.getAsync(applicationId);
      }
    );
  }

  async function deleteAsync() {
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        try {
          await approval.deleteAsync(applicationId, formInputs.comment);
          alert(
            `${application.value?.approvalDocument.name} 신청이 취소 되었습니다.`
          );
          router.back();
        } catch (error) {
          if (error instanceof ApiFlexError && error.value.resultCode == 400) {
            showBackEndFlexErrorMessage(
              '결재 신청 취소 중 오류가 발생하였습니다.',
              error
            );
            return;
          }
          throw error;
        }
      }
    );
  }

  onMounted(() => {
    refreshAsync();
  });
</script>

<template>
  <DefaultLayout
    go-back
    title="나의 신청 목록 / 상세">
    <skeleton
      v-if="loading"
      class="my-5 mb-8" />
    <div
      v-else
      class="border-slate-200 bg-base-200 card card-compact card-bordered w-4/5 mx-auto"
      :class="{
        'bg-neutral border-slate-700': themeStore.isDark,
      }">
      <div class="card-body gap-4">
        <div>
          <h4>신청 유형</h4>
          <p class="pt-1 font-bold">
            {{ application?.approvalDocument.name }}
          </p>
        </div>
        <div>
          <h4>처리 상태</h4>
          <p class="pt-1 font-bold">
            {{ ApprovalStatusMap[application?.status ?? 'REQUEST'] }}
          </p>
        </div>
	      <div>
		      <h4>결재자</h4>
		      <p class="pt-1 font-bold">
			      {{ application?.manager?.name }}
		      </p>
	      </div>
        <div>
          <h4>근무일</h4>
          <p class="pt-1 font-bold">
            {{ dayjs(application?.date).format('YYYY-MM-DD') }}
          </p>
        </div>
        <div>
          <h4>주간 근무 시간</h4>
          <p class="pt-1 font-bold">
            {{ convertSecondsToHoursAndMinutes(application?.dayTime ?? 0) }}
          </p>
        </div>
        <div>
          <h4>야간 근무 시간</h4>
          <p class="pt-1 font-bold">
            {{ convertSecondsToHoursAndMinutes(application?.nightTime ?? 0) }}
          </p>
        </div>
        <div>
          <h4>사유</h4>
          <p class="pt-1 font-bold whitespace-pre-wrap">{{ application?.comment }}</p>
        </div>
        <div v-if="application?.status == 'REJECT'">
          <h4>검토 의견</h4>
          <p class="pt-1 font-bold">{{ application?.approvalLine?.comment }}</p>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-center mt-5 join">
      <button
        v-if="application && !dayjs(application?.date).isAfter(dayjs())"
        class="w-32 join-item btn"
        @click="
          router.push({
            name: 'AttendanceDayView',
            params: {
              targetDate: dayjs(application?.date).format('YYYY-MM-DD'),
              empNo: application?.empNo ?? 0,
            },
          })
        ">
        근무 기록 열람
      </button>
      <button
        v-if="application?.status == 'REQUEST'"
        class="w-32 join-item btn btn-error text-base"
        onclick="delete_application.showModal()">
        회수하기
      </button>
    </div>
  </DefaultLayout>

  <AppModal
    id="delete_application"
    has-close-x>
    <h3 class="pt-2 pb-2">회수 사유</h3>
    <textarea
      v-model="formInputs.comment"
      class="w-full textarea textarea-bordered"
      placeholder="코멘트를 입력하세요." />
    <template #action>
      <button
        class="mt-1 btn btn-block btn-error"
        @click="deleteAsync">
        회수
      </button>
    </template>
  </AppModal>
</template>
